<template>
  <div class="@container">
    <div v-if="$slots.label" class="mb-2 text-sm font-semibold">
      <slot name="label" />
    </div>
    <div
      class="grid divide-neutral-200 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm"
      :class="
        isRightSidebarVisible
          ? '@lg:grid-cols-[0.65fr_0.35fr] @lg:divide-x'
          : 'grid-cols-1'
      "
    >
      <div :class="{ 'hidden @lg:block': isRightSidebarVisible }">
        <FormInput
          v-model="searchInput"
          type="text"
          placeholder="Search..."
          class="p-2"
        />

        <!--Groups on the left side-->
        <div v-if="!searchInput" class="h-[250px] overflow-y-auto p-2">
          <div class="flex flex-col space-y-1">
            <button
              v-for="group in groupWithButtonIds"
              :key="group.buttonId"
              :class="
                twMerge(
                  groupButtonClasses,
                  isGroupSelected(group) && 'bg-neutral-50',
                )
              "
              @click="setSelectedGroup(group)"
              :id="group.buttonId"
              :test_id="group.name"
            >
              <span
                class="inline-flex items-center space-x-2 font-medium text-neutral-600"
              >
                <span class="text-sm">
                  {{ group.name }}
                </span>
                <span
                  :class="
                    twMerge(
                      'hidden h-6 w-6 place-items-center rounded-full bg-neutral-50 text-xs transition-colors group-hover:bg-neutral-100 @sm:grid',
                      isGroupSelected(group) && 'bg-neutral-100',
                    )
                  "
                >
                  {{ group.options.length }}
                </span>
              </span>
              <ChevronRightIcon class="size-4 text-neutral-600" />
            </button>
          </div>
        </div>
      </div>

      <!--Options on the right side-->
      <div v-if="isRightSidebarVisible" class="h-[308px] overflow-y-auto">
        <div class="flex flex-col space-y-1 p-2">
          <button
            @click="setSelectedGroup(null)"
            class="inline-flex items-center justify-start px-3 py-2 font-semibold @lg:hidden"
          >
            <ChevronRightIcon class="mr-2 h-3 w-3 rotate-180" />
            Go back
          </button>
          <button
            v-for="option in selectedGroup?.options"
            :key="option.value"
            :class="
              twMerge(
                optionButtonClasses,
                isOptionSelected(option) &&
                  'border-secondary-500 hover:border-secondary-500',
              )
            "
            @click="selectOption(option)"
            :test_id="option.label"
          >
            <span>
              {{ option.label }}
            </span>
            <CheckIcon
              v-if="isOptionSelected(option)"
              class="text-secondary-500 size-4"
            />
          </button>
        </div>
      </div>

      <!--Results from search input-->
      <div v-if="searchInput" class="h-[250px] overflow-y-auto">
        <div class="space-y-3 p-3">
          <div class="text-xs font-medium text-neutral-400 uppercase">
            Results
          </div>
          <div
            v-if="foundOptionsWithGroups.length"
            class="flex flex-col space-y-1"
          >
            <button
              v-for="(option, index) in foundOptionsWithGroups"
              :key="index"
              :class="
                twMerge(
                  optionButtonClasses,
                  isOptionSelected(option) &&
                    'border-secondary-500 hover:border-secondary-500',
                )
              "
              @click="selectOption(option)"
            >
              <span class="inline-flex items-center space-x-2">
                <span>{{ option.group }}</span>
                <ChevronRightIcon class="size-2" />
                <span>{{ option.label }}</span>
              </span>
              <CheckIcon
                v-if="isOptionSelected(option)"
                class="text-secondary-500 size-4"
              />
            </button>
          </div>
          <div v-else class="text-sm text-neutral-500">
            Nothing found with "{{ searchInput }}"
          </div>

          <div class="text-xs font-medium text-neutral-400 uppercase">
            Groups
          </div>
          <div v-if="foundGroups.length" class="flex flex-col space-y-1">
            <button
              v-for="group in foundGroups"
              :key="group.name"
              :class="groupButtonClasses"
              @click="handleClickGroupFromSearchResults(group, group.buttonId)"
            >
              {{ group.name }}
            </button>
          </div>
          <div v-else class="text-sm text-neutral-500">
            Nothing found with "{{ searchInput }}"
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isError"
      class="text-error-500 mt-2 text-xs"
      aria-live="assertive"
    >
      {{ Array.isArray(errors) ? errors.join(', ') : errors }}
    </div>

    <div v-if="!hideTags" class="mt-4">
      <Tag
        v-for="selectedValue in selectedValuesWithLabels"
        :key="selectedValue.label"
        removable
        color="purple"
        class="mr-1 mb-1"
        @remove="selectOption(selectedValue)"
      >
        {{ selectedValue.label }}
      </Tag>
    </div>
  </div>
</template>

<script setup lang="ts">
import FormInput from '@/shared/components/forms/FormInput.vue'
import { computed, nextTick, ref, useId } from 'vue'
import { twMerge } from 'tailwind-merge'
import CheckIcon from '~icons/lucide/check'
import Tag from '@/shared/components/Tag.vue'
import { matchSorter } from 'match-sorter'
import ChevronRightIcon from '~icons/lucide/chevron-right'

type Option = {
  value: string | number
  label: string
}

type Group = {
  name: string
  options: Option[]
}

type GroupWithButtonId = Group & {
  buttonId: string
}

type OptionWithGroup = Option & {
  group: string
}

type ModelValue = (string | number)[] | string | number

const emit = defineEmits<{
  'update:modelValue': [value: ModelValue]
}>()

const props = withDefaults(
  defineProps<{
    groups: Group[]
    modelValue?: ModelValue
    multiple?: boolean
    hideTags?: boolean
    isError?: boolean
    errors?: string[] | string
  }>(),
  {
    modelValue: () => [],
    multiple: false,
    hideTags: false,
    isError: false,
    errors: '',
  },
)

const isRightSidebarVisible = computed(
  () => selectedGroup.value && !searchInput.value,
)

const groupWithButtonIds = computed(() =>
  props.groups.map((group) => ({
    ...group,
    buttonId: `form-select-group-button-${useId()}`,
  })),
)

const groupButtonClasses =
  'group inline-flex items-center text-left justify-between rounded-lg px-2 py-1 transition-colors hover:bg-neutral-50'
const optionButtonClasses =
  'group inline-flex items-center justify-between text-left rounded-full text-xs @sm:text-sm border border-white px-3 py-2 transition-colors hover:border-neutral-200'

const selectedValuesWithLabels = computed(() => {
  const values = Array.isArray(props.modelValue)
    ? props.modelValue
    : [props.modelValue]
  return values.map((selectedValue) => {
    const group = props.groups.find((group) =>
      group.options.find((option) => option.value === selectedValue),
    )
    const option = group?.options.find(
      (option) => option.value === selectedValue,
    )
    return {
      label: option?.label ?? '',
      value: selectedValue,
    }
  })
})

const selectedGroup = ref<GroupWithButtonId | null>(null)
const setSelectedGroup = (group: GroupWithButtonId | null) => {
  selectedGroup.value = group
}
const isGroupSelected = (group: GroupWithButtonId) => {
  return selectedGroup.value && selectedGroup.value.name === group.name
}
const handleClickGroupFromSearchResults = (
  group: GroupWithButtonId,
  buttonId: string,
) => {
  searchInput.value = ''
  selectedGroup.value = group
  if (!buttonId) return
  nextTick(() => {
    // Focusing the button makes it visible in the viewport
    document.getElementById(buttonId)?.focus()
  })
}

const isOptionSelected = (option: Option) => {
  if (props.multiple && Array.isArray(props.modelValue)) {
    return props.modelValue.includes(option.value)
  }
  return props.modelValue === option.value
}
const selectOption = (option: Option) => {
  if (props.multiple && Array.isArray(props.modelValue)) {
    return isOptionSelected(option)
      ? emit(
          'update:modelValue',
          props.modelValue.filter((value) => value !== option.value),
        )
      : emit('update:modelValue', [...props.modelValue, option.value])
  }

  emit('update:modelValue', option.value)
}

const searchInput = ref('')
const foundOptionsWithGroups = computed(() => {
  const optionsWithGroups = props.groups.reduce<OptionWithGroup[]>(
    (accumulator, currentGroup) => {
      return [
        ...accumulator,
        ...currentGroup.options.map((option) => ({
          group: currentGroup.name,
          label: option.label,
          value: option.value,
        })),
      ]
    },
    [],
  )
  return matchSorter(optionsWithGroups, searchInput.value, {
    keys: ['label'],
  })
})
const foundGroups = computed(() => {
  return matchSorter(groupWithButtonIds.value, searchInput.value, {
    keys: ['name'],
  })
})
</script>
